import React, { useEffect, useState } from "react";
import { useHostPortalContext } from "context/HostPortalContext";
import { useUser } from "context/UserContext";
import { useAlertContext } from "context/AlertContext";
import corporateAccountService from "services/corporateAccountService";
import locationsService from "services/locationsService";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "mdComponents/MDBox";
import MDTypography from "mdComponents/MDTypography";
import { formatDate } from "../Utils";
import { useNavigate } from "react-router-dom";

export interface CorporatePoorConditionReportCount {
  hostId: number;
  hostName: string;
  totalReports: number;
  lastReportedAt: string | null;
}
interface CorporatePoorConditionReportsProps {
  limitToId?: number;
  hideBusinessName?: boolean;
  title?: string;
  description?: string;
  disableClick?: boolean;
}


const CorporatePoorConditionReports =(props: CorporatePoorConditionReportsProps) => {
  const { limitToId, description, title, hideBusinessName, disableClick } = props;
  const navigate = useNavigate();
  const { setHost } = useHostPortalContext();
  const { userCorporateAccount } = useUser();
  const { setAlert } = useAlertContext();

  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [reports, setReports] = useState<CorporatePoorConditionReportCount[]>([]);
  const [showCorporateRollup, setShowCorporateRollup] = useState(true);

  useEffect(() => {
    const loadReports = async () => {
      setLoading(true);
      try {
        let data = await corporateAccountService.getCorporateHostPoorConditionReportsCounts(
          userCorporateAccount?.id
        );
        if (data) {
            if(limitToId){
                data = data.filter(a => a.hostId === limitToId);
            };

          setReports(data);
          setShowCorporateRollup(data.filter((a) => a.hostId !== -1).length > 1);
        } else {
          setAlert("error", "Unable to load poor condition reports");
        }
      } catch (err) {
        console.error(err);
        setAlert("error", "Error loading poor condition reports");
      } finally {
        setLoading(false);
      }
    };
    loadReports();
  }, [userCorporateAccount]);

  const goToReports = async (hostId: number) => {
    if (hostId === -1) return;
    setRedirecting(true);
    const selectedHost = await locationsService.getHostRecord(hostId);
    if (!selectedHost) {
      setRedirecting(false);
      return;
    }
    setHost(selectedHost);
    setRedirecting(false);
    navigate(`/location/poor-condition-reports/${hostId}`);
  };

  const renderReportItem = (item: CorporatePoorConditionReportCount) => {
    const isTotalRow = item.hostId === -1;
    if (isTotalRow && !showCorporateRollup) return null;

    return (
      <Card
        key={item.hostId}
        sx={{
          p: 2,
          mb: 2,
          cursor: isTotalRow ? "default" : "pointer",
          backgroundColor: isTotalRow ? "#e0f7fa" : "#fff",
          width: "100%",
        }}
        onClick={() => goToReports(item.hostId)}
      >
        {!hideBusinessName && 
        <MDTypography variant="h6" fontWeight="bold" mb={1}>
          {isTotalRow ? "Corporate Total" : item.hostName}
        </MDTypography>}

        <MDBox display="flex" justifyContent="space-between">
          <MDBox textAlign="center">
            <MDTypography variant="h6">
              {item.lastReportedAt ? formatDate(item.lastReportedAt, true) : "-"}
            </MDTypography>
            <MDTypography variant="caption">Most Recent Report</MDTypography>
          </MDBox>

          <MDBox textAlign="center">
            <MDTypography variant="h6">{item.totalReports}</MDTypography>
            <MDTypography variant="caption">Total Reports</MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    );
  };

  return (
    <MDBox>
      <MDTypography variant="h5" fontWeight="bold">
       {title ?? 'Poor Condition Reports'}
      </MDTypography>
       <MDTypography variant="button" color="text" mb={1} sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}> 
        {description ?? 'Poor conditions reported by users when using the location.'}
      </MDTypography>

      {redirecting || loading ? (
        <MDBox display="flex" justifyContent="center" mt={4}>
          <CircularProgress sx={{ color: "#21b2a6" }} />
        </MDBox>
      ) : reports.length === 0 ? (
        <MDBox display="flex" justifyContent="center" alignItems="center" mt={4}>
          <MDTypography>No poor condition reports.</MDTypography>
        </MDBox>
      ) : (
        <MDBox>
          {reports.map((item) => renderReportItem(item))}
        </MDBox>
      )}
    </MDBox>
  );
};

export default CorporatePoorConditionReports;
